// src/app/features/warehouse/store/warehouse.effects.ts
//
// In-flight deduplication via local Set (same pattern as claims).
// Searches use switchMap (auto-cancel). Keyed loads use inFlight + cache check.

import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, EMPTY } from 'rxjs';
import { switchMap, map, catchError, withLatestFrom, finalize } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { WarehouseApiService } from '../services/warehouse-api.service';
import { isCacheValid } from './warehouse.state';
import {
  InvoicesActions,
  InvoiceDetailActions,
  ArticlesActions,
  ArticleTypesActions,
  StockActions,
  StockMovementsActions,
  LowStockActions,
  KomitentsActions,
  KomitentDetailActions,
  SummaryActions,
} from './warehouse.actions';
import {
  selectInvoiceDetailMap,
  selectStockMovementsMap,
  selectKomitentDetailMap,
  selectSummary,
} from './warehouse.selectors';

@Injectable()
export class WarehouseEffects {
  private readonly actions$ = inject(Actions);
  private readonly store    = inject(Store);
  private readonly api      = inject(WarehouseApiService);
  private readonly translate = inject(TranslateService);

  private readonly _inFlight = new Set<string>();

  private readError(err: unknown): string {
    const e = err as any;
    return e?.error?.message ?? e?.message ?? this.translate.instant('ERRORS.UNEXPECTED_DOT');
  }

  // ── Invoices (search — switchMap auto-cancels) ────────────────────────────
  searchInvoices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InvoicesActions.search),
      switchMap(({ params }) =>
        this.api.getInvoices(params).pipe(
          map(data => InvoicesActions.success({ data })),
          catchError(err => of(InvoicesActions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── Invoice Detail (keyed by id) ──────────────────────────────────────────
  loadInvoiceDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InvoiceDetailActions.load),
      withLatestFrom(this.store.select(selectInvoiceDetailMap)),
      switchMap(([{ id, forceReload }, detailMap]) => {
        const current = detailMap[id];
        if (!forceReload && current && isCacheValid(current.cache)) return EMPTY;
        const key = `invoiceDetail:${id}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getInvoice(id).pipe(
          map(data => InvoiceDetailActions.success({ id, data })),
          catchError(err => of(InvoiceDetailActions.failure({ id, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  // ── Articles (search) ─────────────────────────────────────────────────────
  searchArticles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ArticlesActions.search),
      switchMap(({ params }) =>
        this.api.getArticles(params).pipe(
          map(data => ArticlesActions.success({ data })),
          catchError(err => of(ArticlesActions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── Article Types (search) ────────────────────────────────────────────────
  searchArticleTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ArticleTypesActions.search),
      switchMap(({ params }) =>
        this.api.getArticleTypes(params).pipe(
          map(data => ArticleTypesActions.success({ data })),
          catchError(err => of(ArticleTypesActions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── Stock (search) ────────────────────────────────────────────────────────
  searchStock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StockActions.search),
      switchMap(({ params }) =>
        this.api.getStock(params).pipe(
          map(data => StockActions.success({ data })),
          catchError(err => of(StockActions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── Stock Movements (keyed by artId) ──────────────────────────────────────
  loadStockMovements$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StockMovementsActions.load),
      withLatestFrom(this.store.select(selectStockMovementsMap)),
      switchMap(([{ artId, params, forceReload }, movementsMap]) => {
        const current = movementsMap[artId];
        if (!forceReload && current && isCacheValid(current.cache)) return EMPTY;
        const key = `stockMovements:${artId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getStockMovements(artId, params).pipe(
          map(data => StockMovementsActions.success({ artId, data })),
          catchError(err => of(StockMovementsActions.failure({ artId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  // ── Low Stock (search) ────────────────────────────────────────────────────
  searchLowStock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LowStockActions.search),
      switchMap(({ params }) =>
        this.api.getLowStock(params).pipe(
          map(data => LowStockActions.success({ data })),
          catchError(err => of(LowStockActions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── Komitents (search) ────────────────────────────────────────────────────
  searchKomitents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(KomitentsActions.search),
      switchMap(({ params }) =>
        this.api.getKomitents(params).pipe(
          map(data => KomitentsActions.success({ data })),
          catchError(err => of(KomitentsActions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── Komitent Detail (keyed by id) ─────────────────────────────────────────
  loadKomitentDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(KomitentDetailActions.load),
      withLatestFrom(this.store.select(selectKomitentDetailMap)),
      switchMap(([{ id, forceReload }, detailMap]) => {
        const current = detailMap[id];
        if (!forceReload && current && isCacheValid(current.cache)) return EMPTY;
        const key = `komitentDetail:${id}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getKomitent(id).pipe(
          map(data => KomitentDetailActions.success({ id, data })),
          catchError(err => of(KomitentDetailActions.failure({ id, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  // ── Summary (cached) ─────────────────────────────────────────────────────
  loadSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SummaryActions.load),
      withLatestFrom(this.store.select(selectSummary)),
      switchMap(([{ forceReload }, slice]) => {
        if (!forceReload && isCacheValid(slice.cache)) return EMPTY;
        return this.api.getSummary().pipe(
          map(data => SummaryActions.success({ data })),
          catchError(err => of(SummaryActions.failure({ error: this.readError(err) }))),
        );
      }),
    )
  );
}
